import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
  openDeleteTimetableModalAction,
  openVersionPanelAction,
  useAppDispatch,
} from '../../../redux';
import { TimetablePriority } from '../../../types/enums';
import {
  mapDayOfWeekToUiName,
  mapTimetablePriorityToUiName,
  useGetLocalizedTextFromDbBlob,
} from '../../../utils/i18n';
import { mapToShortDate } from '../../../utils/time';
import { IconButton } from '../../common/Buttons';
import {
  SimpleDropdownMenu,
  SimpleDropdownMenuItem,
} from '../../common/Dropdowns';
import { Visible } from '../../common/LayoutComponents';
import { TimetableVersionRowData } from './Utils';

const testIds = {
  row: 'TimetableVersionTableRow::row',
  dayType: 'TimetableVersionTableRow::dayType',
  validityStart: 'TimetableVersionTableRow::validityStart',
  validityEnd: 'TimetableVersionTableRow::validityEnd',
  actions: 'TimetableVersionTableRow::actions',
  deleteTimetableMenuItem: 'TimetableVersionTableRow::deleteTimetableMenuItem',
  versionPanelMenuItem: 'TimetableVersionTableRow::versionPanelMenuItem',
};

function getStatusClassName({
  priority,
  inEffect,
}: {
  priority: TimetablePriority;
  inEffect?: boolean;
}) {
  if (inEffect) {
    return 'bg-hsl-dark-green text-white';
  }
  const statusClassNames: Record<TimetablePriority, string> = {
    [TimetablePriority.Standard]: 'bg-brand text-white',
    [TimetablePriority.Temporary]: 'bg-city-bicycle-yellow',
    [TimetablePriority.Special]: 'bg-border-hsl-commuter-train-purple', // Background purple would be too light in this context.
    [TimetablePriority.SubstituteByLineType]: 'bg-hsl-orange',
    [TimetablePriority.Draft]: 'bg-background',
    [TimetablePriority.Staging]: 'bg-hsl-red',
  };
  return statusClassNames[priority];
}

function getDayTypeClassName(priority: TimetablePriority) {
  const dayTypeClassNames: Record<TimetablePriority, string> = {
    [TimetablePriority.Standard]: 'bg-hsl-dark-green/25',
    [TimetablePriority.Temporary]: 'bg-city-bicycle-yellow/25',
    [TimetablePriority.Special]: 'bg-border-hsl-commuter-train-purple/25',
    [TimetablePriority.SubstituteByLineType]: 'bg-hsl-orange/25',
    [TimetablePriority.Draft]: 'bg-background/25',
    [TimetablePriority.Staging]: 'bg-hsl-red/25',
  };
  return dayTypeClassNames[priority];
}

type TimetableVersionTableRowProps = {
  readonly data: TimetableVersionRowData;
};

export const TimetableVersionTableRow: FC<TimetableVersionTableRowProps> = ({
  data,
}) => {
  const { t } = useTranslation();
  const getLocalizedTextFromDbBlob = useGetLocalizedTextFromDbBlob();

  const dispatch = useAppDispatch();

  const onClick = () => {
    if (data.vehicleScheduleFrame.id) {
      dispatch(openDeleteTimetableModalAction(data.vehicleScheduleFrame.id));
    }
  };

  const openVersionPanel = () => {
    if (data.vehicleScheduleFrame.id) {
      dispatch(
        openVersionPanelAction({
          vehicleScheduleFrameId: data.vehicleScheduleFrame.id,
        }),
      );
    }
  };

  const statusClassName = ` ${getStatusClassName({
    priority: data.vehicleScheduleFrame.priority,
    inEffect: data.inEffect,
  })}`;

  const statusText = data.inEffect
    ? t(($) => $.timetables.inEffect)
    : mapTimetablePriorityToUiName(t, data.vehicleScheduleFrame.priority);

  const substituteDayOperatingText = data.substituteDay?.substituteDayOfWeek
    ? t(($) => $.timetables.operatedLike, {
        dayOfWeek: mapDayOfWeekToUiName(
          t,
          data.substituteDay.substituteDayOfWeek,
        ),
      })
    : t(($) => $.timetables.noService);

  const dayType = getLocalizedTextFromDbBlob(data.dayType.nameI18n);
  const vehicleScheduleFrameName = getLocalizedTextFromDbBlob(
    data.vehicleScheduleFrame.nameI18n,
  );

  return (
    <tr
      className="h-14 text-center [&>td]:border [&>td]:border-light-grey"
      data-testid={testIds.row}
    >
      <td className={statusClassName}>{statusText}</td>
      <td
        className={getDayTypeClassName(data.vehicleScheduleFrame.priority)}
        data-testid={testIds.dayType}
      >
        {dayType}
        <Visible visible={!!data.vehicleScheduleFrame.id}>
          <IconButton
            onClick={openVersionPanel}
            tooltip={t(($) => $.accessibility.timetables.showTimetable)}
            icon={<i className="icon-calendar mx-2" />}
          />
        </Visible>
        {data.substituteDay?.supersededDate && (
          <span className="flex justify-center text-xs whitespace-nowrap">
            {substituteDayOperatingText}
          </span>
        )}
      </td>
      <td data-testid={testIds.validityStart}>
        {mapToShortDate(data.vehicleScheduleFrame.validityStart)}
      </td>
      <td data-testid={testIds.validityEnd}>
        {mapToShortDate(data.vehicleScheduleFrame.validityEnd)}
      </td>
      <td>{data.routeLabelAndVariant}</td>
      <td>{vehicleScheduleFrameName}</td>
      <td>!Muokkaaja</td>
      <td>!Muokattu</td>
      <td>
        <Visible visible={!!data.vehicleScheduleFrame.id}>
          <SimpleDropdownMenu
            testId={testIds.actions}
            tooltip={t(($) => $.accessibility.timetables.versionActions, {
              status: statusText,
              schedule: vehicleScheduleFrameName,
              dayType,
            })}
          >
            <SimpleDropdownMenuItem
              onClick={onClick}
              testId={testIds.deleteTimetableMenuItem}
              text={t(($) => $.timetables.versions.deleteTimetable)}
            />
            <SimpleDropdownMenuItem
              onClick={openVersionPanel}
              testId={testIds.versionPanelMenuItem}
              text={t(($) => $.timetables.versions.showInfo)}
            />
          </SimpleDropdownMenu>
        </Visible>
      </td>
    </tr>
  );
};
